import '../work-order.scss';
import classNames from 'classnames';
import FileSaver from 'file-saver';
import Icon from '../../../components/icons/icon';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  UseabilityStatus,
  WorkOrder,
  WorkOrderSearchParams,
  WorkOrderStatus,
} from '../../../clients/quipli-service/service-and-repair/service-and-repair';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { WorkOrderForm } from './work-order-form';
import { DateTime } from 'luxon';
import { PaginationData } from '../../../clients/quipli-service/pagination/pagination';
import { DataTable, DataTableRow, DataTableColumn } from '../../../components/table/data-table';
import { ProductImage } from '../../../components/image/product/product-image';
import { Typography } from '../../../components/typography/typography';
import { SearchBar } from '../../../components/search-bar/search-bar';
import { Button } from '../../../components/form/button';
import { Dropdown } from '../../../components/dropdown/dropdown';
import { Badge } from '../../../components/badge/badge';
import { useSearchParams } from 'react-router-dom';
import { SelectForm } from '../../../components/select-form/select-form';
import { Location } from '../../../clients/quipli-service/location/location';
import useRequestQueryParams from '../../../hooks/use-request-query-params';
import { ProductImageV2 } from '../../../components/image/product/product-image-v2';
import { QuipliLink } from '../../../components/link/quipli-link';
import { User, UserEquipment } from '../../../clients/quipli-service/user/user';

export const WorkOrderList = ({ title = 'Work Orders', user }: { title?: string; user?: User }) => {
  const { searchWorkOrders, listLocations, exportWorkOrders, getMultipleConfigurations } = useAPI();

  // States
  const [isFormOpen, openFormModal] = useState<boolean>(false);
  const [isListLoading, setListLoading] = useState<boolean>(true);
  const [data, setData] = useState<DataTableRow[]>([]);
  const [pagination, setPagination] = useState<PaginationData>();
  const [unitTrackingField, setUnitTrackingField] = useState<'unitNumber' | 'serialNumber'>();
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location>();
  const [productImagesV2, setProductImagesV2] = useState<boolean>();
  const [tableColumns, setTableColumns] = useState<DataTableColumn[]>([]);

  // List request hooks
  const [params] = useSearchParams();
  const { request, setRequest } = useRequestQueryParams<WorkOrderSearchParams>(
    {
      keyword: params.has('keyword') ? params.get('keyword')! : undefined,
      locationIds: params.has('locationId') ? [parseInt(params.get('locationId')!)] : undefined,
      workOrderTab: params.has('workOrderTab')
        ? (params.get('workOrderTab')! as 'current' | 'history' | 'late' | 'upcoming')
        : 'current',
      pageSize: params.has('pageSize') ? parseInt(params.get('pageSize')!) : 10,
      pageNumber: params.has('pageNumber') ? parseInt(params.get('pageNumber')!) : 1,
    },
    async (_request) => {
      setListLoading(true);

      const { keyword, workOrderTab: tab, locationIds, pageSize, pageNumber } = _request;

      const workOrderStatuses = statusesforTab(tab);

      const { pagination, items } = await searchWorkOrders({
        keyword: keyword,
        locationIds: locationIds,
        statuses: workOrderStatuses,
        userId: user?.id,
        from: tab === 'upcoming' ? DateTime.now().startOf('day') : undefined,
        to: tab === 'late' ? DateTime.now().startOf('day') : undefined,
        sortBy: tab === 'history' ? 'updatedAt' : 'from',
        sortDirection: tab === 'history' ? 'desc' : 'asc',
        pageSize,
        pageNumber,
      });

      setPagination(pagination);
      setData(items.map(buildWorkOrderDataRow));
      setTableColumns(workOrderColumns);

      setListLoading(false);
    },
    [unitTrackingField],
  );

  const statusesforTab = (tab: string | undefined): string[] => {
    return tab === 'current'
      ? [WorkOrderStatus.InProgress.valueOf(), WorkOrderStatus.OnHold.valueOf()]
      : tab === 'late' || tab === 'upcoming'
      ? [WorkOrderStatus.NotStarted.valueOf()]
      : [WorkOrderStatus.Completed, WorkOrderStatus.Cancelled];
  };

  const formatUserEquipmentDescription = (userEquipment: UserEquipment) => {
    let description = userEquipment.make ? `${userEquipment.make} ${userEquipment.model}` : userEquipment.description;
    if (description.length > 50) {
      description = `${description.substring(0, 50)}...`;
    }

    return description;
  };

  // Vars
  const workOrderColumns: DataTableColumn[] = [
    { key: 'orderNumber', label: 'Order No.' },
    { key: 'product', label: 'Product/Equipment' },
    { key: 'unit', label: 'Unit/Serial #' },
    { key: 'type', label: 'Work Type' },
    { key: 'status', label: 'Status' },
    { key: 'from', label: 'Work From' },
    { key: 'to', label: 'Work To' },
    { key: 'useability', label: 'Unit Availability' },
    { key: 'mechanic', label: 'Mechanic' },
    { key: 'latestNoteDate', label: 'Latest Note Date' },
    { key: 'latestNote', label: 'Latest Note' },
  ];

  // Callbacks
  const buildWorkOrderDataRow = (wo: WorkOrder): DataTableRow => {
    const end = wo.actualEnd || wo.to;

    return {
      mobileHeader: (
        <>
          <div className="product-container">
            {wo.product && (
              <div className="image">
                {productImagesV2 ? (
                  <ProductImageV2 productId={wo.product.id} profile="MINI" />
                ) : (
                  <ProductImage productId={wo.product.id} profile="MINI" />
                )}{' '}
              </div>
            )}
            <Typography type="text-sm" className="text-quipli-black-800">
              {wo.product && (
                <QuipliLink
                  to={`workorders/view/${wo.id}`}
                  className="underline underline-offset-1 text-[var(--quipli-black-800)]"
                >
                  {wo.product.name}
                </QuipliLink>
              )}
              {!wo.product && <>{wo.equipmentDescription}</>}
            </Typography>
          </div>
        </>
      ),
      fields: {
        orderNumber: (
          <Typography type="text-sm" weight="regular">
            <QuipliLink
              to={`workorders/view/${wo.id}`}
              className="underline underline-offset-1 text-[var(--quipli-black-800)]"
            >
              {wo.orderNumber ?? 'N/A'}
            </QuipliLink>
          </Typography>
        ),
        product: (
          <div className="product-container">
            {wo.product && (
              <div className="image">
                {productImagesV2 ? (
                  <ProductImageV2 productId={wo.product.id} profile="MINI" />
                ) : (
                  <ProductImage productId={wo.product.id} profile="MINI" />
                )}{' '}
              </div>
            )}
            <Typography type="text-sm" className="truncate text-quipli-black-800">
              {wo.product?.name && wo.product.name}
              {!wo.product && (
                <>
                  <div>{wo.user && wo.user.name}</div>
                  <div>{wo.userEquipment && formatUserEquipmentDescription(wo.userEquipment)}</div>
                </>
              )}
            </Typography>
          </div>
        ),
        unit: (
          <Typography type="text-sm" className="truncate text-quipli-black-800">
            {wo.unit && wo.unit[unitTrackingField!]}
            {wo.userEquipment && wo.userEquipment.serialNumber && `${wo.userEquipment.serialNumber}`}
            {!wo.unit && !wo.userEquipment && '-'}
          </Typography>
        ),
        type: wo.type?.label ?? '-',
        status: wo.status ?? '-',
        from: (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {wo.from.toFormat('DD')}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              {wo.from.toFormat('t')}
            </Typography>
          </div>
        ),
        to: end ? (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {end.toFormat('DD')}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              {end.toFormat('t')}
            </Typography>
          </div>
        ) : (
          'TBD'
        ),
        useability: (
          <>
            {wo.unitStatus && (
              <Badge text={wo.unitStatus} type={wo.unitStatus === UseabilityStatus.HardDown ? 'danger' : 'warning'} />
            )}
            {!wo.unitStatus && (
              <Typography type="text-sm" className="text-quipli-black-800">
                N/A
              </Typography>
            )}
          </>
        ),
        mechanic: (
          <Typography type="text-sm" className="text-quipli-black-800">
            {wo.mechanic?.adminFullName || 'N/A'}
          </Typography>
        ),
        latestNoteDate: wo.latestNote ? (
          <div className="flex flex-col">
            <Typography type="text-sm" className="text-quipli-black-800">
              {wo.latestNote.uploadedAt.toFormat('DD')}
            </Typography>
            <Typography type="text-sm" className="text-grey-500">
              {wo.latestNote.uploadedAt.toFormat('t')}
            </Typography>
          </div>
        ) : undefined,
        latestNote: (
          <Typography type="text-sm" className="text-quipli-black-800 whitespace-pre-line">
            {wo.latestNote?.note || wo.latestNote?.file}
          </Typography>
        ),
        id: wo.id,
      },
      className: 'cursor-pointer',
      redirectTo: `workorders/view/${wo.id}`,
    };
  };

  const exportToExcel = async () => {
    const workOrderStatuses = statusesforTab(request.workOrderTab);
    const file = await exportWorkOrders({
      ...request,
      userId: user?.id,
      statuses: workOrderStatuses,
      from: request.workOrderTab === 'upcoming' ? DateTime.now().startOf('day') : undefined,
      to: request.workOrderTab === 'late' ? DateTime.now().startOf('day') : undefined,
    });
    const exportFileName = `Work_Orders_${DateTime.local().toFormat('D').replaceAll('/', '-')}`;
    const fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const fileExtension = '.xlsx';

    const data = new Blob([file], { type: fileType });
    FileSaver.saveAs(data, `${exportFileName}${fileExtension}`);
  };

  // Effects
  useEffect(() => {
    const get = async () => {
      const [configs, _locations] = await Promise.all([
        getMultipleConfigurations(['CONF_UNIT_TRACKING_VIA', 'CONF_PRODUCT_IMAGES_V2']),
        listLocations(),
      ]);
      setUnitTrackingField(
        configs.find((c) => c.variable === 'CONF_UNIT_TRACKING_VIA')?.value === '1' ? 'serialNumber' : 'unitNumber',
      );
      setProductImagesV2(configs.find((c) => c.variable === 'CONF_PRODUCT_IMAGES_V2')?.value === '1');

      setLocations(_locations);
      setSelectedLocation(
        params.has('locationId') ? _locations.find((l) => l.id === parseInt(params.get('locationId')!)) : undefined,
      );
    };

    get();
  }, []);

  return (
    <div className="work-order">
      <div className="container-v2">
        <div className="flex justify-between items-center">
          <Typography type="display-sm" weight="medium">
            {title}
          </Typography>

          <Options className="block md:hidden" addNew={user ? undefined : openFormModal} exportList={exportToExcel} />
        </div>

        <div className="pt-5 flex items-center justify-between">
          <SearchBar
            placeholder={'Search Work Orders'}
            defaultValue={request.keyword}
            setRequest={setRequest}
            subtitle="Product, Unit, Customer, Email, Equipment, Order Number, Parts, Services"
          />

          <Options className="hidden md:block" addNew={user ? undefined : openFormModal} exportList={exportToExcel} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 items-center gap-2 py-5 filters-wrapper">
          <SelectForm
            options={locations}
            onChange={(l) => {
              setSelectedLocation(l || undefined);
              setRequest((prevState) => ({ ...prevState, locationIds: l ? [l.id] : undefined }));
            }}
            getOptionLabel={(l) => l.name}
            getOptionValue={(l) => `${l.id}`}
            isClearable={true}
            placeholder="Location"
            value={selectedLocation}
          />
        </div>

        <DataTable
          isLoading={isListLoading}
          columnNames={tableColumns}
          data={data}
          pagination={pagination}
          request={request}
          setRequest={setRequest}
          tabs={request.workOrderTab && <Tabs tab={request.workOrderTab} setRequest={setRequest} />}
        />
      </div>

      <WorkOrderForm isOpen={isFormOpen} setIsOpen={openFormModal} />
    </div>
  );
};

const Tabs = ({
  tab,
  setRequest,
}: {
  tab: 'current' | 'history' | 'late' | 'upcoming';
  setRequest: Dispatch<SetStateAction<WorkOrderSearchParams>>;
}) => {
  const [currentItemsTab, setCurrentItemsTab] = useState<'current' | 'history' | 'late' | 'upcoming'>(tab);

  const handleFilter = (tabName: 'current' | 'history' | 'late' | 'upcoming') => {
    if (tabName == currentItemsTab) return;

    setRequest((prevState) => ({
      ...prevState,
      workOrderTab: tabName,
      pageNumber: 1,
    }));
    setCurrentItemsTab(tabName);
  };

  return (
    <div className="list-tabs">
      <div className="tabs-wrapper ml-3">
        <div
          className={classNames('tab-item', { active: currentItemsTab === 'current' })}
          onClick={() => handleFilter('current')}
        >
          <Typography
            type="text-sm"
            weight="medium"
            className={currentItemsTab === 'current' ? 'text-quipli-black-800' : 'text-grey-500'}
          >
            Current
          </Typography>
          <div className="divider-active"></div>
        </div>
        <div
          className={classNames('tab-item', { active: currentItemsTab === 'upcoming' })}
          onClick={() => handleFilter('upcoming')}
        >
          <Typography
            type="text-sm"
            weight="medium"
            className={currentItemsTab === 'upcoming' ? 'text-quipli-black-800' : 'text-grey-500'}
          >
            Upcoming
          </Typography>
          <div className="divider-active"></div>
        </div>
        <div
          className={classNames('tab-item', { active: currentItemsTab === 'late' })}
          onClick={() => handleFilter('late')}
        >
          <Typography
            type="text-sm"
            weight="medium"
            className={currentItemsTab === 'late' ? 'text-quipli-black-800' : 'text-grey-500'}
          >
            Past Due
          </Typography>
          <div className="divider-active"></div>
        </div>
        <div
          className={classNames('tab-item', { active: currentItemsTab === 'history' })}
          onClick={() => handleFilter('history')}
        >
          <Typography
            type="text-sm"
            weight="medium"
            className={currentItemsTab === 'history' ? 'text-quipli-black-800' : 'text-grey-500'}
          >
            History
          </Typography>
          <div className="divider-active"></div>
        </div>
      </div>
    </div>
  );
};

const Options = ({
  className,
  addNewText = 'Add Work Order',
  addNew,
  exportList,
}: {
  className?: string;
  addNewText?: string;
  addNew?: (open: boolean) => void;
  exportList?: () => void;
}) => {
  return (
    <div className={className}>
      <Dropdown
        trigger={() => (
          <Button className="button-tertiary">
            <Icon icon="DotsVertical" />
          </Button>
        )}
      >
        {addNew && (
          <div
            className="items-center gap-2"
            onClick={() => {
              addNew(true);
            }}
          >
            <Icon icon="Add" size="medium" />
            <Typography type="text-sm" className="text-quipli-black-800">
              {addNewText}
            </Typography>
          </div>
        )}

        {exportList && (
          <div
            className="items-center gap-2"
            onClick={() => {
              exportList();
            }}
          >
            <Icon icon="Export" size="medium" />
            <Typography type="text-sm" className="text-quipli-black-800">
              Export
            </Typography>
          </div>
        )}
      </Dropdown>
    </div>
  );
};
